import React = require("react");
import { LoadingPageOverlay } from "../../../designSystem/LoadingPageOverlay";
import { timeToClose, timeToEnd, timeToOpen, timeToStart } from "../../../utils/date";
import { rng } from '../../../amorg-common';
import * as dateMath from 'date-arithmetic';
import { txt } from "../../../txt";
import { ActivityType } from '../../../amorg-common';
import { ActivityListingView } from "../common/ActivityListingView";
import { SurveyActivityListing } from '../../../amorg-common';
import { getSurveyActivities, createActivity } from "../../../api/activityClientApi";
import { AmorgMenuContent, LeadingContentType } from "../../../designSystem/amorgMenu";
import { Redirect } from "react-router-dom";
import { GenericActivityListingView } from "../common/GenericActivityListingView";
import { SingleColumnLayout } from "../../../designSystem/layouts/SingleColumnLayout";

type State = {
	loading:boolean,
	activities:SurveyActivityListing[],
    redirectId:string
}
export function WorkplaceAssessmentListingView(): JSX.Element
{
	const [state, setState] = React.useState<State>({
        loading:true,
        activities:[],
        redirectId:''
    });

    React.useEffect(()=>{
        const getData = async () => {
            const activities = await getSurveyActivities('WorkplaceAssessment');
            activities.sort((a,b)=> b.start.getTime() - a.start.getTime());
            // Get activities convert start and end from strings (JSON transport) to dates
            setState({activities, loading:false, redirectId:''});
        }
        getData();
    },[])

    async function handleClickNew()
    {
        const now = new Date();

        const activityId = rng.getRandomId();
        const startDate = new Date(now.getFullYear(), now.getMonth()+1, 1, 8, 0);
        const endDate = dateMath.add(startDate, 1, 'month');
        const type:ActivityType = 'WorkplaceAssessment';

        setState({...state, loading:true});

        const result = await createActivity(
            activityId,
            'WorkplaceAssessment',
            txt.getDynamic(`NewActivityType_${type}`),
            '',
            startDate,
            endDate,
            ''
        );

        if(result.isSuccess){
            setState({...state, redirectId:activityId});
        }
    }

	return (
        <SingleColumnLayout
            noDivider
            contentContainer={
                <>
                    {state.redirectId !== '' &&
                        <Redirect to={`/workplaceassessments/${state.redirectId}`} />
                    }

                    <LoadingPageOverlay loading={state.loading} />
                    
                    <GenericActivityListingView
                        headerIcon='interface_user_userOK'
                        headerText="Aktuelle APV'er"
                        pastHeaderText="Tidligere APV'er"
                        newActivityButtonText="Opret ny APV"
                        activities={state.activities}
                        onClick={(id) => setState({...state, redirectId:id})}
                        onClickNew={handleClickNew} />
                </>
            }
        />
	);
}

type SurveyActivityListingViewProps = {
    activity:SurveyActivityListing
}

export function SurveyActivityListingView({activity}:SurveyActivityListingViewProps):JSX.Element
{
    return (
        <ActivityListingView
            title={activity.title}
            start={activity.start}
            end={activity.end}
            href={`/workplaceassessments/${activity.id}`} 
            menuProps={{
                content:[
                    getSurveyMenuItemProps(activity),
                    getAnswerMenuItemProps(activity),
                    getActionPlanMenuItemProps(activity)
            ]}}
        />
    );
}

function getSurveyMenuItemProps(activity:SurveyActivityListing):AmorgMenuContent
{
    // No survey
    if(activity.survey === null)
        return {
            type:'leadingTrailingContent',
            styleVariant:'destructive',
            leadingContent:{ type:'icon', iconName:'actions_close' },
            primaryContent:{ type:'text', text:'Intet spørgeskema'},
            trailingContent:{ type:'text', text:'' }
        };

    // Survey incomplete
    if(activity.survey.questionCount === 0)
        return {
            type:'leadingTrailingContent',
            styleVariant:'destructive',
            leadingContent:{ type:'icon', iconName:'actions_close' },
            primaryContent:{ type:'text', text:'Spørgeskema ufærdigt'},
            trailingContent:{ type:'text', text:'' }
        };

    
    return {
        type:'leadingTrailingContent',
        styleVariant:'default',
        leadingContent:{ type:'icon', iconName:'actions_done' },
        primaryContent:{ type:'text', text:'Spørgeskema oprettet'},
        trailingContent:{ type:'text', text:`${activity.survey.questionCount} spørgsmål` }
    };
}

function getAnswerMenuItemProps(activity:SurveyActivityListing):AmorgMenuContent
{
    // No survey
    if(activity.survey === null)
        return {
            type:'leadingTrailingContent',
            styleVariant:'destructive',
            leadingContent:{ type:'icon', iconName:'actions_close' },
            primaryContent:{ type:'text', text:'Ingen besvarelser'},
            trailingContent:{ type:'text', text:'' }
        };

    // Has survey
    else{
        
        const now = new Date();

        let leadingContent:LeadingContentType = {
            type:'icon', iconName:'actions_close'
        }
        if(activity.survey.answerCount > 0){
            if(activity.end > now)
                leadingContent = {
                    type:'icon', iconName:'interface_glyph_hourglass'
                }
            else
                leadingContent = {
                    type:'icon', iconName:'actions_done'
                }
        }

        let labelText = 'Ingen besvarelser';
        if(activity.survey.answerCount > 0)
            labelText = `${activity.survey.answerCount} besvarelser`;

        let trailingText = '';
        if(activity.survey.start > now)
            trailingText = `${timeToOpen(activity.survey.start)}`;
        else if(activity.survey.end > now)
            trailingText = `${timeToClose(activity.survey.end)}`;
        else
            trailingText = 'Lukket for besvarelser';
        
        
        return {
            type:'leadingTrailingContent',
            styleVariant:activity.survey.answerCount > 0 ? 'default' : 'destructive',
            leadingContent,
            primaryContent:{ type:'text', text:labelText},
            trailingContent:{ type:'text', text:trailingText }
        };
    }
}

function getActionPlanMenuItemProps(activity:SurveyActivityListing):AmorgMenuContent
{
    let leadingContent:LeadingContentType =
        { type:'icon', iconName:'actions_close' };
    if(activity.actionPlanCount > 0)
        leadingContent = { type:'icon', iconName:'actions_done' }

    let labelText = 'Ingen handleplan';
    if(activity.actionPlanCount === 1)
        labelText = `${activity.actionPlanCount} handleplan oprettet`;
    if(activity.actionPlanCount > 1)
        labelText = `${activity.actionPlanCount} handleplaner oprettet`;

    const now = new Date();

    let trailingText = '';
    if(activity.start > now){
        trailingText = `Periode ${timeToStart(activity.start).toLowerCase()}`;
    }
    else{
        if(activity.end > now){
            trailingText = `Periode ${timeToEnd(activity.end).toLowerCase()}`;
        }
        else
            trailingText = `Periode er afsluttet`;
    }

    return {
        type:'leadingTrailingContent', 
        styleVariant:activity.actionPlanCount > 0 ? 'default' : 'destructive',
        leadingContent,
        primaryContent:{ type:'text', text:labelText},
        trailingContent:{ type:'text', text:trailingText }
    };
}